/**
 * Traduccion del perfil que devuelve `GET /auth/me`.
 *
 * Misma divergencia que en `masters`: la SPA modela en ingles
 * (`memberships`, `role`, `tenantName`) y el backend en espanol (`alcances`,
 * `rol`, `tenant_nombre`). No es conversion de casing — los VOCABULARIOS DE
 * ROL son distintos: `accountant` alla es `contador`, `viewer` es `consulta`.
 * Un conversor automatico los dejaria pasar tal cual y la SPA no reconoceria
 * ninguno, que es peor que fallar: los permisos se leerian vacios y las
 * pantallas se verian "sin acceso" sin decir por que.
 *
 * Por eso la tabla se escribe a mano y se verifica con tests.
 */
import type { Capability, Membership, Role, UserProfile } from "@/lib/types"

/** Rol del backend -> rol de la SPA. */
const ROL_A_ROLE: Record<string, Role> = {
  platform_admin: "platform_admin",
  contador: "accountant",
  cfo: "cfo",
  gerencia: "client_management",
  administracion: "client_finance",
  consulta: "viewer",
  auditor: "external_auditor",
}

/** El inverso, para cuando la SPA tenga que mandar un rol. */
export const ROLE_A_ROL: Record<Role, string> = Object.fromEntries(
  Object.entries(ROL_A_ROLE).map(([rol, role]) => [role, rol]),
) as Record<Role, string>

/** Las capacidades SI coinciden. Se verifica en tests para que siga asi. */
const CAPACIDADES: ReadonlySet<string> = new Set<Capability>([
  "read",
  "edit",
  "approve",
  "close_period",
  "sign",
])

export interface AlcanceRow {
  rol: string
  /**
   * `null` en el alcance GLOBAL del admin de plataforma. Estaba declarado
   * `string` y no lo es: ver `membershipsFromApi`, donde esa mentira de tipo
   * dejaba entrar una membresia sin tenant y colgaba la entrada del producto.
   */
  tenant_id: string | null
  tenant_nombre: string | null
  entity_id: string | null
  capacidades: string[]
  vigente_hasta: string | null
  pais?: string | null
}

export interface PerfilRow {
  id: string
  email: string
  activo: boolean
  es_global: boolean
  alcances: AlcanceRow[]
  nombre?: string | null
  /** `null` = sin elegir. Ver `UserProfile.idioma`. */
  idioma?: "es" | "pt" | "en" | null
}

/**
 * Un alcance por (tenant, rol) del backend se vuelve un `Membership`.
 *
 * El backend puede devolver varias filas del mismo tenant —un usuario que es
 * contador Y cfo de la misma empresa, que es el caso real de este producto—.
 * Se agrupan por tenant: la SPA razona por tenant, no por asignacion, y dos
 * entradas del mismo tenant le harian mostrar el selector de empresa duplicado.
 *
 * PERO AGRUPAR NO ES DESCARTAR. Antes se conservaba UN solo rol —el de mayor
 * alcance— y se unian las capacidades. Las capacidades sobrevivian; el rol
 * perdido no. Y el rol es lo que decide QUE SECCION se ve (`lib/secciones.ts`):
 * al gatear el menu, Contabilidad desaparecia para alguien que si es contador,
 * solo porque tambien era CFO y CFO gana la precedencia. Por eso el rol
 * principal ahora es DERIVADO de la lista y la lista es lo que se guarda.
 */
export function membershipsFromApi(alcances: AlcanceRow[]): Membership[] {
  const porTenant = new Map<string, Membership>()

  for (const a of alcances ?? []) {
    const role = ROL_A_ROLE[a.rol]
    if (!role) continue // rol que la SPA no conoce: se ignora, no se inventa

    /*
      UNA FILA SIN TENANT NO ES UNA MEMBRESIA, Y ESTO COLGABA LA ENTRADA.

      El admin de plataforma llega como un alcance con `tenant_id: null`, y el
      backend lo devuelve PRIMERO. Aqui se agrupaba por `a.tenant_id` como
      cualquier otra fila, asi que nacia una membresia con `tenantId: null` a la
      cabeza de la lista. `decidirTenant` toma `memberships[0].tenantId` como
      cliente activo, con lo que el cliente activo quedaba en `null` para
      siempre; y `Entrada` espera con «hay cartera y todavia no hay cliente»,
      que es justo ese estado. Resultado: quien es admin de plataforma entraba
      bien, la sesion se cargaba bien, y se quedaba en «Viendo por donde vas…»
      sin error, sin peticion fallida y sin salida.

      El alcance global no se pierde: viaja en `globalRoles` y en `es_global`,
      que es donde siempre significo lo que significa.
    */
    if (!a.tenant_id) continue

    const capabilities = (a.capacidades ?? []).filter((c): c is Capability =>
      CAPACIDADES.has(c),
    )
    const existente = porTenant.get(a.tenant_id)

    if (!existente) {
      porTenant.set(a.tenant_id, {
        tenantId: a.tenant_id,
        tenantName: a.tenant_nombre ?? "",
        roles: [role],
        role,
        capabilities,
        entityIds: a.entity_id ? [a.entity_id] : undefined,
        // Una fila SIN entidad es alcance sobre el tenant entero. Se acumula
        // con OR entre filas: basta una para tenerlo, y una fila acotada que
        // llegue despues no se lo quita.
        tenantWide: !a.entity_id,
        country: a.pais ?? undefined,
      })
      continue
    }

    for (const c of capabilities) {
      if (!existente.capabilities.includes(c)) existente.capabilities.push(c)
    }
    if (a.entity_id) {
      existente.entityIds = [...(existente.entityIds ?? []), a.entity_id]
    } else {
      existente.tenantWide = true
    }
    if (!existente.roles.includes(role)) {
      existente.roles.push(role)
      // La lista se mantiene ordenada por alcance y el principal es su cabeza:
      // asi `role` no depende del orden en que el backend devolvio las filas.
      existente.roles.sort((x, y) => PRECEDENCIA.indexOf(x) - PRECEDENCIA.indexOf(y))
      existente.role = existente.roles[0]
    }
  }

  return [...porTenant.values()]
}

/**
 * Los roles que NO cuelgan de ningun tenant: el alcance global del admin de
 * plataforma. Se devuelven aparte porque no son una membresia —no hay cliente
 * al que pertenezcan— pero si gatean el menu, y meterlos en `memberships` es
 * exactamente lo que colgaba la entrada.
 */
export function globalRolesFromApi(alcances: AlcanceRow[]): Role[] {
  const roles = new Set<Role>()
  for (const a of alcances ?? []) {
    if (a.tenant_id) continue
    const role = ROL_A_ROLE[a.rol]
    if (role) roles.add(role)
  }
  return [...roles].sort((x, y) => PRECEDENCIA.indexOf(x) - PRECEDENCIA.indexOf(y))
}

/**
 * De mas a menos alcance. Ordena `roles` y con eso elige el rol PRINCIPAL, que
 * es solo la etiqueta de pantalla: ninguna decision de acceso mira esta lista.
 */
const PRECEDENCIA: Role[] = [
  "platform_admin",
  "cfo",
  "accountant",
  "client_management",
  "client_finance",
  "external_auditor",
  "viewer",
]

export function profileFromApi(row: PerfilRow): UserProfile {
  return {
    // El backend serializa `id: "None"` cuando el usuario todavia no esta
    // vinculado en `app_user`. No se propaga esa cadena: se deja vacio.
    uid: row.id && row.id !== "None" ? row.id : "",
    email: row.email,
    displayName: row.nombre || row.email,
    global: Boolean(row.es_global),
    memberships: membershipsFromApi(row.alcances),
    globalRoles: globalRolesFromApi(row.alcances),
    // `?? null` y no `?? "es"`: un backend viejo que no manda el campo deja al
    // usuario SIN preferencia —que es la verdad— en vez de declararle una.
    idioma: row.idioma ?? null,
  }
}
